import React from "react";
import PageLayout from "@/components/layout/PageLayout";
import { Hero, Section, SectionHeader } from "@/components/layout/Sections";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Building2, Zap, Clock, Plus, Minus } from "lucide-react";

const FAQS = [
  {
    q: "Per chi è pensato il noleggio a lungo termine?",
    a: "Il noleggio a medio e lungo termine è la soluzione ottimale per chiunque voglia ottimizzare la gestione della propria autovettura o del proprio parco automezzi, perché permette di avere veicoli sempre nuovi e in ottime condizioni, senza dover investire capitali iniziali e senza la preoccupazione dei costi di gestione, degli imprevisti e della svalutazione del veicolo quando sarà il momento di sostituirlo.",
  },
  {
    q: "Che vantaggi offre il noleggio a lungo termine?",
    a: "Con il noleggio a medio e lungo termine ottimizzi l'organizzazione e la pianificazione dei costi e dei servizi perché utilizzi il veicolo che hai scelto a un canone fisso mensile, senza nessuna variazione.",
  },
  {
    q: "Cosa è compreso nel canone di noleggio a lungo termine?",
    a: "All'interno del canone di noleggio a medio e lungo termine di un'auto o di un veicolo commerciale sono compresi: assicurazione completa, manutenzione ordinaria e straordinaria, pneumatici estivi ed invernali, veicolo sostitutivo in caso di necessità, gestione sinistri e rinotifica contravvenzioni.",
  },
  {
    q: "Come si calcola il canone fisso mensile?",
    a: "Il canone mensile viene calcolato in base al tipo di veicolo scelto, che può essere configurato come vuoi tu, alla durata del noleggio, ai Km inseriti e ai servizi scelti.",
  },
];

function Faq({ q, a, i, open, onToggle }) {
  return (
    <div className="border-b border-white/15">
      <button
        onClick={() => onToggle(i)}
        className="w-full py-5 flex items-start justify-between gap-6 text-left group"
        data-testid={`faq-${i}`}
      >
        <span className="font-display uppercase text-lg md:text-xl leading-tight group-hover:text-[color:var(--nsa-orange)] transition-colors">
          {q}
        </span>
        <span className="shrink-0 mt-1 text-[color:var(--nsa-orange)]">
          {open ? <Minus size={22} /> : <Plus size={22} />}
        </span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <p className="pb-6 text-white/75 leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Noleggio() {
  const [openFaq, setOpenFaq] = React.useState(0);
  const toggle = (i) => setOpenFaq((p) => (p === i ? -1 : i));

  return (
    <PageLayout>
      <Hero
        image="/showroom/auto.png"
        eyebrow="Auto · Noleggio"
        title={<>Noleggio Auto<br />su misura</>}
        subtitle="Soluzioni personalizzate per privati e aziende. Scopri il noleggio a breve, medio o lungo termine con NSA Motors."
      />

      {/* Intro */}
      <Section>
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div>
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">Le nostre formule</span>
            <h2 className="font-display uppercase tracking-tight text-4xl md:text-5xl mt-3 leading-[0.95]">
              Noleggi a breve,<br />medio e lungo termine
            </h2>
          </div>
          <div className="text-gray-700 leading-relaxed space-y-4">
            <p>
              Oltre alla vendita, siamo specializzati nel <strong>noleggio a breve, medio o lungo termine</strong>
              &nbsp;di veicoli nuovi e usati, in pronta consegna o su ordinazione.
            </p>
            <p>
              Siamo partner ufficiali <strong>MERCURY</strong>, società leader di mercato per questo servizio innovativo
              ed in forte espansione.
            </p>
          </div>
        </div>
      </Section>

      {/* Lungo termine — feature block */}
      <Section tone="dark">
        <div className="grid md:grid-cols-5 gap-10 md:gap-16 items-start">
          <div className="md:col-span-2">
            <div className="flex items-center justify-center h-12 w-12 mb-5 bg-[color:var(--nsa-orange)] text-white">
              <Building2 size={22} />
            </div>
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">Aziende & professionisti</span>
            <h2 className="font-display uppercase text-4xl md:text-5xl mt-3 text-white leading-[0.95]">
              Noleggio<br />a medio e lungo termine
            </h2>
          </div>
          <div className="md:col-span-3 text-white/80 leading-relaxed space-y-4">
            <p>
              Il noleggio auto a medio e lungo termine rappresenta una soluzione ottimale per <strong>aziende, società,
              artigiani, agenti di commercio, dirigenti e consulenti</strong> che hanno bisogno di un veicolo sempre
              nuovo e performante.
            </p>
            <p>
              Rispetto all'acquisto, questa formula ti permette di <strong>programmare e ottimizzare i costi</strong> e
              la gestione del mezzo, di avere un <strong>canone mensile compreso di assistenza</strong>, manutenzione,
              tagliandi, cambio gomme e assicurazioni, e di sostituire periodicamente il parco auto aziendale senza
              farti carico di costi anticipati.
            </p>
            <p>
              Inoltre, se sottoscrivi un noleggio a lungo termine, sei tu che <strong>scegli l'auto che vuoi</strong>:
              potrai scegliere il modello, gli allestimenti, i colori e tutti gli optional che preferisci. Proprio come
              se stessi acquistando un'auto nuova!
            </p>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16">
          <span className="label-eyebrow text-[color:var(--nsa-orange)]">Diamo risposta alle tue domande</span>
          <h3 className="font-display uppercase text-3xl md:text-4xl text-white mt-3 leading-tight max-w-3xl">
            Noleggio a medio e lungo termine: le domande frequenti
          </h3>
          <div className="mt-8 max-w-4xl">
            {FAQS.map((f, i) => (
              <Faq key={i} i={i} q={f.q} a={f.a} open={openFaq === i} onToggle={toggle} />
            ))}
          </div>
        </div>
      </Section>

      {/* Breve termine */}
      <Section tone="gray">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div>
            <div className="flex items-center justify-center h-12 w-12 mb-5 bg-white text-[color:var(--nsa-orange)] border border-gray-200">
              <Clock size={22} />
            </div>
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">Anche per un giorno</span>
            <h2 className="font-display uppercase tracking-tight text-4xl md:text-5xl mt-3 leading-[0.95]">
              Noleggio<br />a breve termine
            </h2>
            <div className="mt-6 text-gray-700 leading-relaxed space-y-4">
              <p>
                Disponiamo di <strong>veicoli in pronta consegna</strong> per il noleggio a breve termine,
                <strong> anche solo per un giorno</strong>.
              </p>
              <p>
                Devi fare un trasloco e hai bisogno di un furgone? Hai in programma un viaggio e vuoi un'auto più adatta
                alla situazione? Devi spostarti con amici e parenti e ti serve un Van 9 posti?
              </p>
              <p>
                Da noi trovi auto e veicoli commerciali che puoi <strong>noleggiare per brevi periodi</strong> di tempo,
                e vivere i tuoi spostamenti in serenità.
              </p>
            </div>
          </div>
          <div className="aspect-[4/3] bg-cover bg-center" style={{ backgroundImage: "url(/showroom/auto.png)" }} />
        </div>
      </Section>

      {/* Elettrico */}
      <Section>
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div className="aspect-[4/3] bg-cover bg-center md:order-2" style={{ backgroundImage: "url(/showroom/noleggio-elettrico.jpg)" }} data-testid="noleggio-elettrico-photo" />
          <div>
            <div className="flex items-center justify-center h-12 w-12 mb-5 bg-gray-100 text-[color:var(--nsa-orange)]">
              <Zap size={22} />
            </div>
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">Ecologici & silenziosi</span>
            <h2 className="font-display uppercase tracking-tight text-4xl md:text-5xl mt-3 leading-[0.95]">
              Scopri la magia<br />del noleggio elettrico
            </h2>
            <div className="mt-6 text-gray-700 leading-relaxed space-y-4">
              <p>
                Noleggiare un'auto elettrica ti offre un sacco di vantaggi: dai <strong>bassi costi di ricarica</strong>,
                fino alla possibilità di entrare in <strong>ZTL e centri cittadini</strong> senza richiedere permessi.
              </p>
              <p>
                Noleggiando le nostre auto elettriche, proverai l'esperienza di guidare con l'energia,
                <strong> senza emissioni, in silenzio e nel comfort più totale</strong>.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA finale */}
      <Section tone="gray">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">Preventivo su misura</span>
            <h2 className="font-display uppercase text-4xl md:text-5xl mt-3">Progettiamo insieme il tuo noleggio</h2>
          </div>
          <Link
            to="/contatti#form"
            className="btn-orange label-eyebrow px-6 py-4 inline-flex items-center gap-3"
            data-testid="noleggio-cta"
          >
            Richiedi un preventivo <ArrowRight size={16} />
          </Link>
        </div>
      </Section>
    </PageLayout>
  );
}
